import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../assets/ticketsTable.module.css';
import ticketsService from '../services/ticketsService';

// Helper function to format date to dd/mm/yyyy
const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  // If already formatted as dd/mm/yyyy, return as is
  if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
    return dateValue;
  }
  // Parse ISO date or other formats
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const TicketsTable = ({ propertyId, month, year, onMonthYearChange }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [togglingClientView, setTogglingClientView] = useState(null); // Track which ticket is being toggled
  const [useMockData, setUseMockData] = useState(false); // Always use live data for Monthly Inspection Reports

  // DataTable controls state
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    status: true,
    urgency: true,
    propertyId: true,
    propertyName: true,
    postcode: true,
    submitted: true,
    deadline: true,
    completed: true,
    jobType: true,
    clientView: true,
    clientFirst: true,
    clientLast: true,
    contractor: true,
    actions: true,
  });

  // Column definitions for visibility dropdown
  const columnDefinitions = [
    { key: 'id', label: 'ID' },
    { key: 'status', label: 'Status' },
    { key: 'urgency', label: 'Urgency' },
    { key: 'propertyId', label: 'Property ID' },
    { key: 'propertyName', label: 'Property Name' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'submitted', label: 'Submitted' },
    { key: 'deadline', label: 'Deadline' },
    { key: 'completed', label: 'Completed' },
    { key: 'jobType', label: 'Job Type' },
    { key: 'clientView', label: 'Client View' },
    { key: 'clientFirst', label: 'C. First' },
    { key: 'clientLast', label: 'C. Last' },
    { key: 'contractor', label: 'Contractor' },
    { key: 'actions', label: 'Actions' },
  ];

  // Mock data generation
  const generateMockTickets = () => {
    const mockTickets = [];
    for (let i = 1; i <= 25; i++) {
      mockTickets.push({
        id: i,
        status: ['Open', 'In Progress', 'Completed', 'Closed'][Math.floor(Math.random() * 4)],
        urgency: ['Low', 'Medium', 'High', 'Critical'][Math.floor(Math.random() * 4)],
        property_id: `PROP${String(i).padStart(3, '0')}`,
        property_name: `Property ${i}`,
        postcode: `SW${String(i).padStart(2, '0')} ${String(i).padStart(2, '0')}AA`,
        submitted: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB'),
        deadline: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB'),
        completed: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB') : null,
        job_type: ['Maintenance', 'Repair', 'Inspection', 'Emergency'][Math.floor(Math.random() * 4)],
        client_view: Math.random() > 0.3 ? 'Yes' : 'No',
        client_first: ['John', 'Jane', 'Mike', 'Sarah'][Math.floor(Math.random() * 4)],
        client_last: ['Smith', 'Doe', 'Johnson', 'Brown'][Math.floor(Math.random() * 4)],
        contractor: ['ABC Contractors', 'XYZ Services', 'Quick Fix Ltd'][Math.floor(Math.random() * 3)],
      });
    }
    return mockTickets;
  };

  // Load tickets - Always use live data for Monthly Inspection Reports
  useEffect(() => {
    loadTickets();
  }, [propertyId, currentPage, month, year]); // Added month and year dependencies

  const loadTickets = async () => {
    setLoading(true);
    try {
      // Always use live data for Monthly Inspection Reports
      console.log("🔍 [FRONTEND] Loading tickets for property:", propertyId, "month:", month, "year:", year);
      
      // Import tickets service
      const ticketsService = await import('../services/ticketsService');
      
      // Get tickets for the specific property with month/year filtering
      const params = {};
      if (month && month !== '') {
        params.month = month;
      }
      if (year && year !== '') {
        params.year = year;
      }
      
      const response = await ticketsService.default.getTicketsByProperty(propertyId, params);
      
      console.log("🔍 [FRONTEND] Tickets API response:", response);
      
      if (response && response.success && response.data) {
        setTickets(response.data);
        setTotalPages(Math.ceil(response.data.length / 10)); // Assuming 10 per page
        console.log("✅ [FRONTEND] Loaded tickets from live database:", response.data.length);
      } else {
        console.log("⚠️ [FRONTEND] No tickets found for property:", propertyId);
        setTickets([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      console.log("❌ [FRONTEND] Failed to load tickets, showing empty table");
      setTickets([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Open':
        return styles.statusOpen;
      case 'In Progress':
        return styles.statusInProgress;
      case 'Completed':
        return styles.statusCompleted;
      case 'Closed':
        return styles.statusClosed;
      default:
        return '';
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency) {
      case 'Low':
        return styles.urgencyLow;
      case 'Medium':
        return styles.urgencyMedium;
      case 'High':
        return styles.urgencyHigh;
      case 'Critical':
        return styles.urgencyCritical;
      default:
        return '';
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewTicket = (ticketId) => {
    console.log("🔍 [FRONTEND] Viewing ticket:", ticketId);
    // Navigate to ticket details page
    navigate(`/tickets/${ticketId}`);
  };

  const handleEditTicket = (ticketId) => {
    console.log("🔍 [FRONTEND] Editing ticket:", ticketId);
    // Navigate to ticket edit page
    navigate(`/tickets/${ticketId}/edit`);
  };

  // Handle toggling the client_view checkbox (calls API like Laravel)
  const handleToggleClientView = async (ticketId, currentValue) => {
    const newValue = !currentValue;
    console.log(`🔍 [FRONTEND] Toggling client_view for ticket ${ticketId} from ${currentValue} to ${newValue}`);

    setTogglingClientView(ticketId);

    try {
      await ticketsService.toggleClientView(ticketId, newValue ? 1 : 0);

      // Update local state to reflect the change
      setTickets(prevTickets =>
        prevTickets.map(ticket =>
          ticket.id === ticketId
            ? { ...ticket, client_view: newValue ? 1 : 0 }
            : ticket
        )
      );
      console.log(`✅ [FRONTEND] Client view toggled successfully for ticket ${ticketId}`);
    } catch (error) {
      console.error(`❌ [FRONTEND] Error toggling client view for ticket ${ticketId}:`, error);
      alert('Failed to update client view. Please try again.');
    } finally {
      setTogglingClientView(null);
    }
  };

  // Helper to check if client_view is enabled
  const isClientViewEnabled = (ticket) => {
    return ticket.can_client_see_ticket === 'Yes' ||
           ticket.client_view === 'Yes' ||
           ticket.client_view === 1 ||
           ticket.client_view === true;
  };

  // Filter tickets based on search term
  const filteredTickets = tickets.filter(ticket => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      String(ticket.id).toLowerCase().includes(search) ||
      (ticket.statusName || ticket.status || '').toLowerCase().includes(search) ||
      (ticket.urgencyName || ticket.urgency || '').toLowerCase().includes(search) ||
      (ticket.propertyPublicId || ticket.property_id || '').toLowerCase().includes(search) ||
      (ticket.propertyName || ticket.property_name || '').toLowerCase().includes(search) ||
      (ticket.property?.postcode || ticket.postcode || '').toLowerCase().includes(search) ||
      (ticket.serviceName || ticket.job_type || '').toLowerCase().includes(search) ||
      (ticket.contactUser?.first_name || ticket.user?.first_name || ticket.contact_first_name || '').toLowerCase().includes(search) ||
      (ticket.contactUser?.last_name || ticket.user?.last_name || ticket.contact_last_name || '').toLowerCase().includes(search) ||
      (ticket.contractorName || ticket.contractor || '').toLowerCase().includes(search) ||
      (ticket.submitted || '').toLowerCase().includes(search) ||
      (ticket.date_estimated_completion || '').toLowerCase().includes(search) ||
      (ticket.date_completed || '').toLowerCase().includes(search)
    );
  });

  // Paginate filtered tickets
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Update total pages when filtered tickets change
  useEffect(() => {
    setTotalPages(Math.ceil(filteredTickets.length / entriesPerPage) || 1);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [searchTerm, entriesPerPage, filteredTickets.length]);

  // Toggle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle entries per page change
  const handleEntriesChange = (e) => {
    setEntriesPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return (
      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.pageButton}
        >
          Previous
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className={styles.pageButton}
            >
              1
            </button>
            {startPage > 2 && <span className={styles.pageEllipsis}>...</span>}
          </>
        )}
        
        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`${styles.pageButton} ${page === currentPage ? styles.activePage : ''}`}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className={styles.pageEllipsis}>...</span>}
            <button
              onClick={() => handlePageChange(totalPages)}
              className={styles.pageButton}
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.pageButton}
        >
          Next
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        Loading tickets...
      </div>
    );
  }

  return (
    <div className={styles.ticketsTable}>
      <div className={styles.tableInfo}>
        <p>In the report will be displayed only tickets from chosen month and year. These tickets must have selected "Client view" option.</p>
        <p className={styles.tableNote}>Changing "Month" or "Year" field will refresh DataTable.</p>
      </div>

      {/* DataTable Controls */}
      <div className={styles.tableControls}>
        <div className={styles.entriesControl}>
          <label>Show</label>
          <select
            value={entriesPerPage}
            onChange={handleEntriesChange}
            className={styles.entriesSelect}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>entries</span>
        </div>

        <div className={styles.searchControl}>
          <label>Search:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
            placeholder="Search tickets..."
          />
        </div>

        <div className={styles.columnVisibilityControl}>
          <button
            type="button"
            className={styles.columnVisibilityButton}
            onClick={() => setShowColumnVisibility(!showColumnVisibility)}
          >
            Column visibility ▼
          </button>
          {showColumnVisibility && (
            <div className={styles.columnVisibilityDropdown}>
              {columnDefinitions.map(col => (
                <label key={col.key} className={styles.columnVisibilityOption}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={() => toggleColumnVisibility(col.key)}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {visibleColumns.id && <th>ID</th>}
              {visibleColumns.status && <th>Status</th>}
              {visibleColumns.urgency && <th>Urgency</th>}
              {visibleColumns.propertyId && <th>Property ID</th>}
              {visibleColumns.propertyName && <th>Property Name</th>}
              {visibleColumns.postcode && <th>Postcode</th>}
              {visibleColumns.submitted && <th>Submitted</th>}
              {visibleColumns.deadline && <th>Deadline</th>}
              {visibleColumns.completed && <th>Completed</th>}
              {visibleColumns.jobType && <th>Job Type</th>}
              {visibleColumns.clientView && <th>Client View</th>}
              {visibleColumns.clientFirst && <th>C. First</th>}
              {visibleColumns.clientLast && <th>C. Last</th>}
              {visibleColumns.contractor && <th>Contractor</th>}
              {visibleColumns.actions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedTickets.map((ticket) => (
              <tr key={ticket.id}>
                {visibleColumns.id && <td>{ticket.id}</td>}
                {visibleColumns.status && (
                  <td>
                    <span className={`${styles.status} ${getStatusClass(ticket.statusName || ticket.status)}`}>
                      {ticket.statusName || ticket.status}
                    </span>
                  </td>
                )}
                {visibleColumns.urgency && (
                  <td>
                    <span className={`${styles.urgency} ${getUrgencyClass(ticket.urgencyName || ticket.urgency)}`}>
                      {ticket.urgencyName || ticket.urgency}
                    </span>
                  </td>
                )}
                {visibleColumns.propertyId && <td>{ticket.propertyPublicId || ticket.property_id}</td>}
                {visibleColumns.propertyName && <td>{ticket.propertyName || ticket.property_name}</td>}
                {visibleColumns.postcode && <td>{ticket.property?.postcode || ticket.propertyPostcode || ticket.postcode || '-'}</td>}
                {visibleColumns.submitted && <td>{formatDate(ticket.submitted || ticket.created_at)}</td>}
                {visibleColumns.deadline && <td>{formatDate(ticket.date_estimated_completion)}</td>}
                {visibleColumns.completed && <td>{formatDate(ticket.date_completed)}</td>}
                {visibleColumns.jobType && <td>{ticket.serviceName || ticket.job_type}</td>}
                {visibleColumns.clientView && (
                  <td className={styles.clientViewCell}>
                    <input
                      type="checkbox"
                      checked={isClientViewEnabled(ticket)}
                      onChange={() => handleToggleClientView(ticket.id, isClientViewEnabled(ticket))}
                      disabled={togglingClientView === ticket.id}
                      className={styles.clientViewCheckbox}
                    />
                  </td>
                )}
                {visibleColumns.clientFirst && <td>{ticket.contactUser?.first_name || ticket.user?.first_name || ticket.contact_first_name || '-'}</td>}
                {visibleColumns.clientLast && <td>{ticket.contactUser?.last_name || ticket.user?.last_name || ticket.contact_last_name || '-'}</td>}
                {visibleColumns.contractor && <td>{ticket.contractorName || ticket.contractor?.name || ticket.contractor || '-'}</td>}
                {visibleColumns.actions && (
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionButton}
                        title="View"
                        onClick={() => handleViewTicket(ticket.id)}
                      >
                        👁️
                      </button>
                      <button
                        className={styles.actionButton}
                        title="Edit"
                        onClick={() => handleEditTicket(ticket.id)}
                      >
                        ✏️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Info */}
      <div className={styles.tableFooterInfo}>
        Showing {filteredTickets.length === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1} to {Math.min(currentPage * entriesPerPage, filteredTickets.length)} of {filteredTickets.length} entries
        {searchTerm && ` (filtered from ${tickets.length} total entries)`}
      </div>
      
      {renderPagination()}
    </div>
  );
};

export default TicketsTable; 